import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { useState } from "react";
import {
  MAX_TABLE_ROWS,
  qritAccountColumns,
  stpAccountColumns,
} from "../data/constants";
import { app } from "../signals/AppSignal";
import { parsePlan } from "../utils/StringUtil";

interface IAccount {
  accountId: string;
  accountName: string;
  slug: string;
  appId: string;
  appName: string;
  userId: string;
  userName: string;
  userEmail: string;
  installDate: string;
  subscriptionDate: string;
  renewalDate: string;
  isTrial: boolean;
  isTrialStatus: string;
  planId: string | null;
  credit: number;
  usedCredits: number;
  appVersion: string;
  totalRecipe: number;
  totalCreate: number;
  totalScanned: number;
  totalScannedFailed: number;
  totalScannedSuccess: number;
  isTrialEnded: boolean;
  recipe: { total: number; active: number; inactive: number };
  qrgenerated: {
    total: number;
    active: number;
    inactive: number;
  };
  qrscanhistory: {
    total: number;
    success: number;
    failed: number;
  };
}

const Accounts = () => {
  useSignal(0);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagnition, setPagnation] = useState<any>({});

  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 20 });

  const rowCount = accounts.length || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize + 1;
  const endIndex = Math.min(
    (paginationModel.page + 1) * paginationModel.pageSize,
    rowCount,
  );

  const getTablePage = (totalPages: number) => {
    const pages: React.ReactNode[] = [];
    const currentPage = pagnition.currentPage;

    // Controls how many pages to show before and after the current page
    const neighbors = 2;

    for (let i = 1; i <= totalPages; i++) {
      // Always show the first page, the last page, and pages immediately surrounding the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - neighbors && i <= currentPage + neighbors)
      ) {
        const isCurrent = currentPage === i;
        pages.push(
          <li key={i} className="page-item">
            <button
              disabled={isCurrent}
              className={
                isCurrent
                  ? "page-link text-white fw-bold active"
                  : "page-link text-dark"
              }
              onClick={() => getAccounts(i)}
            >
              {i}
            </button>
          </li>,
        );
      }
      // Add Ellipsis if we are skipping chunks of pages
      else if (
        i === currentPage - neighbors - 1 ||
        i === currentPage + neighbors + 1
      ) {
        pages.push(
          <li key={`ellipsis-${i}`} className="page-item disabled">
            <span className="page-link text-muted bg-light border-0">...</span>
          </li>,
        );
      }
    }

    return pages;
  };

  const handlePageSizeChange = (event: { target: { value: string } }) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0, // Reset to first page if pageSize changes
    }));
  };

  const handleNext = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, pageCount - 1),
    }));
  };

  const handlePrev = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const getPlanClass = (status: string) => {
    switch (status) {
      case "free":
        return "status-completed";
      case "basic":
        return "status-pending";
      case "pro":
        return "status-cancelled";
      case "ultimate":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getAccounts = async (page: number = 1) => {
    setLoading(true);
    setAccounts([]);
    axios
      .get(
        `/api/v1/customers/${app.value}/accounts?limit=${MAX_TABLE_ROWS}&page=${page}`,
      )
      .then((response) => {
        // handle success
        setPagnation(response.data.pagination);
        setPaginationModel({
          page: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
        });

        let accountList: IAccount[] = response.data.data || [];
        accountList = accountList.map((list, index) => ({
          ...list,
          id: index + 1,
          totalRecipe: list.recipe?.active || 0,
          totalCreate: list.qrgenerated?.active || 0,
          totalScanned: list.qrscanhistory?.total || 0,
          totalScannedFailed: list.qrscanhistory?.failed || 0,
          totalScannedSuccess: list.qrscanhistory?.success || 0,
          credit: list.isTrialEnded ? 0 : list.credit,
          isTrialStatus: list.isTrial
            ? list.isTrialEnded
              ? "ended"
              : "trial"
            : parsePlan(app.value, list.planId) === "essential"
              ? "subscribed"
              : "ended",
          balance:
            parsePlan(app.value, list.planId) !== "ultimate"
              ? list.credit - list.usedCredits
              : "∞",
          planId: parsePlan(app.value, list.planId),
          installDate: convertUTCToLocal(list.installDate),
          subscriptionDate: convertUTCToLocal(list.subscriptionDate),
          renewalDate: convertUTCToLocal(list.renewalDate),
        }));
        setAccounts(accountList);
      })
      .catch((error) => {
        // handle error
        console.log(error);
        setAccounts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAppValue = () => {
    switch (app.value) {
      case "eam":
        setColumnVisibilityModel({
          isTrialStatus: false,
          totalRecipe: false,
          usedCredits: false,
          totalCreate: false,
          totalScanned: false,
          totalScannedFailed: false,
          totalScannedSuccess: false,
          isTrial: false,
          isTrialEnded: false,
        });
        break;
      case "qrit":
        setColumnVisibilityModel({
          isTrialStatus: false,
          isTrial: false,
          isTrialEnded: false,
        });
        break;
      case "smarttd":
      case "smartwn":
        setColumnVisibilityModel({
          isTrial: false,
          usedCredits: false,
          totalCreate: false,
          totalScanned: false,
          totalScannedFailed: false,
          totalScannedSuccess: false,
          balance: false,
        });
        break;
      case "stp":
        setColumnVisibilityModel({
          totalCreate: false,
          totalScanned: false,
          totalScannedFailed: false,
          totalScannedSuccess: false,
        });
        break;
      default:
        setColumnVisibilityModel({});
        break;
    }
  };

  const getColumnType = (app: string) => {
    switch (app) {
      case "qrit":
        return qritAccountColumns;
      case "stp":
        return stpAccountColumns;
      default:
        return qritAccountColumns;
    }
  };

  useSignalEffect(() => {
    getAccounts();
    getAppValue();
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Accounts</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getAccounts()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-4 row align-items-center compact-controls">
              <div className="col-auto d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm select-list"
                  onChange={handlePageSizeChange}
                  value={paginationModel.pageSize}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="col d-flex align-items-center gap-2">
                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handlePrev}
                  disabled={paginationModel.page === 0}
                >
                  Previous
                </button>

                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handleNext}
                  disabled={paginationModel.page >= pageCount - 1}
                >
                  Next
                </button>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  <span style={{ lineHeight: 1 }}>
                    Page {startIndex}–{endIndex} of {rowCount}
                  </span>
                </div>
              </div>

              <div className="col d-flex align-items-center justify-content-end">
                <nav aria-label="Table navigation">
                  <ul className="pagination pagination-sm mb-0 align-items-center">
                    {/* Previous Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={pagnition?.currentPage === 1}
                        onClick={() => {
                          const prev = pagnition?.currentPage - 1;
                          if (prev >= 1) getAccounts(prev);
                        }}
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>

                    {/* Dynamic Page List */}
                    {getTablePage(pagnition?.totalPages || 0)}

                    {/* Next Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={
                          pagnition?.currentPage === pagnition?.totalPages
                        }
                        onClick={() => {
                          const next = pagnition?.currentPage + 1;
                          if (next <= pagnition?.totalPages) getAccounts(next);
                        }}
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={accounts}
                columns={getColumnType(app.value)}
                hideFooter={true}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) =>
                  setPaginationModel(newModel)
                }
                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel: any) =>
                  setColumnVisibilityModel(newModel)
                }
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accounts;
